import { useCallback, useState } from 'react';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { logger } from 'src/utils/logger';
import { getApiErrorMessage } from 'src/utils/CommonUtils';
import { ListTaskExecutionsResponse } from 'src/@types/selfServeTypes/listTaskExecutions.interface';
import { ChildExecutionData } from './subWorkflowExecutionGraphLayout';

export interface SubWorkflowExpandTarget {
	childWorkflowId: string;
	childWorkflowExecutionId: string;
	workflowName?: string;
}

export interface UseSubWorkflowExecutionsResult {
	expandedExecutionIds: Set<string>;
	childExecutions: Map<string, ChildExecutionData>;
	loadingExecutionIds: Set<string>;
	errorsByExecutionId: Map<string, string>;
	toggleExpansion: (target: SubWorkflowExpandTarget) => void;
}

export function useSubWorkflowExecutions(): UseSubWorkflowExecutionsResult {
	const [expandedExecutionIds, setExpandedExecutionIds] = useState<Set<string>>(
		new Set()
	);
	const [childExecutions, setChildExecutions] = useState<
		Map<string, ChildExecutionData>
	>(new Map());
	const [loadingExecutionIds, setLoadingExecutionIds] = useState<Set<string>>(
		new Set()
	);
	const [errorsByExecutionId, setErrorsByExecutionId] = useState<
		Map<string, string>
	>(new Map());

	const fetchChildExecution = useCallback(
		(target: SubWorkflowExpandTarget) => {
			const { childWorkflowId, childWorkflowExecutionId, workflowName } = target;

			setLoadingExecutionIds((prev) =>
				new Set(prev).add(childWorkflowExecutionId)
			);
			setErrorsByExecutionId((prev) => {
				const next = new Map(prev);

				next.delete(childWorkflowExecutionId);

				return next;
			});

			callSNOWDataService({
				path: `/workflows/${childWorkflowId}/executions/${childWorkflowExecutionId}/tasks`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			} as never)
				.then((response: ListTaskExecutionsResponse) => {
					setChildExecutions((prev) =>
						new Map(prev).set(childWorkflowExecutionId, {
							taskExecutions: response?.taskExecutions ?? [],
							dependencies: response?.dependencies ?? [],
							workflowName,
						})
					);
					setExpandedExecutionIds((prev) =>
						new Set(prev).add(childWorkflowExecutionId)
					);
				})
				.catch((error) => {
					logger.error(
						`Failed to load sub-workflow execution ${childWorkflowExecutionId}: ${JSON.stringify(
							error
						)}`
					);
					setErrorsByExecutionId((prev) =>
						new Map(prev).set(
							childWorkflowExecutionId,
							getApiErrorMessage(error)
						)
					);
				})
				.finally(() => {
					setLoadingExecutionIds((prev) => {
						const next = new Set(prev);

						next.delete(childWorkflowExecutionId);

						return next;
					});
				});
		},
		[]
	);

	const toggleExpansion = useCallback(
		(target: SubWorkflowExpandTarget) => {
			const { childWorkflowExecutionId } = target;

			if (expandedExecutionIds.has(childWorkflowExecutionId)) {
				setExpandedExecutionIds((prev) => {
					const next = new Set(prev);

					next.delete(childWorkflowExecutionId);

					return next;
				});

				return;
			}

			if (childExecutions.has(childWorkflowExecutionId)) {
				setExpandedExecutionIds((prev) =>
					new Set(prev).add(childWorkflowExecutionId)
				);

				return;
			}

			if (!loadingExecutionIds.has(childWorkflowExecutionId)) {
				fetchChildExecution(target);
			}
		},
		[expandedExecutionIds, childExecutions, loadingExecutionIds, fetchChildExecution]
	);

	return {
		expandedExecutionIds,
		childExecutions,
		loadingExecutionIds,
		errorsByExecutionId,
		toggleExpansion,
	};
}
